/**
 * `<CustomThemesSettings>` — the "Themes" tab in Settings (2026-06-01, Chris ask).
 *
 * Lists every user-authored theme in the workspace. Each row has Apply / Edit /
 * Duplicate / Export / Delete actions. A "+ New from…" dropdown clones a built-
 * in seed theme. "Import…" reads a JSON file the user dropped.
 *
 * Build-in themes (ember, blue-slate, parchment, slate, etc.) are NOT listed
 * here — they live in the existing ``<ThemeSettings>`` swatch picker. This
 * panel is exclusively for user-authored overlays.
 */

import { useRef, useState } from 'react';
import { useUiStore } from '@/stores';
import { SettingsCard } from './SettingsCard';
import { SEED_THEMES, type EditableTokenKey, getSeedTheme } from '@/theme/seeds';
import {
  useCustomThemesQuery,
  useCreateCustomThemeMutation,
  useUpdateCustomThemeMutation,
  useDeleteCustomThemeMutation,
} from '@/hooks/useCustomThemes';
import type { CustomTheme } from '@/types/wire';
import { ThemeEditor } from './ThemeEditor';
import { getLogger } from '@/api';

const log = getLogger('settings.themes');

/** Shape of the JSON file the import/export pair uses. Versioned so we can
 *  evolve later without breaking old files. */
interface ThemeExportV1 {
  version: 1;
  name: string;
  seedThemeId: string;
  tokens: Record<string, string>;
}

function exportThemeToJson(theme: CustomTheme): void {
  const doc: ThemeExportV1 = {
    version: 1,
    name: theme.name,
    seedThemeId: theme.seedThemeId,
    tokens: theme.tokens,
  };
  const blob = new Blob([JSON.stringify(doc, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${theme.name.replace(/[^a-z0-9-_]+/gi, '_')}.aletheia-theme.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function parseThemeJson(text: string): ThemeExportV1 | string {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (err) {
    return `Not valid JSON: ${String(err)}`;
  }
  if (typeof raw !== 'object' || raw === null) return 'JSON root must be an object.';
  const obj = raw as Record<string, unknown>;
  if (obj.version !== 1) return `Unsupported version ${String(obj.version)}; expected 1.`;
  if (typeof obj.name !== 'string' || obj.name.trim().length === 0) {
    return '`name` is required.';
  }
  if (typeof obj.seedThemeId !== 'string' || obj.seedThemeId.length === 0) {
    return '`seedThemeId` is required.';
  }
  if (typeof obj.tokens !== 'object' || obj.tokens === null) {
    return '`tokens` is required and must be an object.';
  }
  const tokens: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj.tokens as Record<string, unknown>)) {
    if (typeof k === 'string' && typeof v === 'string') tokens[k] = v;
  }
  return {
    version: 1,
    name: obj.name,
    seedThemeId: obj.seedThemeId,
    tokens,
  };
}

export function CustomThemesSettings(): JSX.Element {
  const themes = useCustomThemesQuery();
  const createMut = useCreateCustomThemeMutation();
  const updateMut = useUpdateCustomThemeMutation();
  const deleteMut = useDeleteCustomThemeMutation();
  const setCustomTheme = useUiStore((s) => s.setCustomTheme);
  const activeCustomThemeId = useUiStore((s) => s.activeCustomThemeId);
  const currentTheme = useUiStore((s) => s.theme);

  const [editing, setEditing] = useState<CustomTheme | null>(null);
  const [creatingFromSeed, setCreatingFromSeed] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onCreateFromSeed = (seedId: string): void => {
    setCreatingFromSeed(seedId);
  };

  const onSaveNew = async (
    name: string,
    tokens: Record<EditableTokenKey, string>,
  ): Promise<void> => {
    if (creatingFromSeed === null) return;
    const created = await createMut.mutateAsync({
      name,
      seedThemeId: creatingFromSeed,
      tokens,
    });
    setCreatingFromSeed(null);
    // Auto-apply the new theme so the user immediately sees the result.
    setCustomTheme(created.id);
  };

  const onSaveEdit = async (
    name: string,
    tokens: Record<EditableTokenKey, string>,
  ): Promise<void> => {
    if (editing === null) return;
    await updateMut.mutateAsync({ themeId: editing.id, name, tokens });
    setEditing(null);
  };

  const onDelete = async (theme: CustomTheme): Promise<void> => {
    if (!window.confirm(`Delete "${theme.name}"? This can't be undone.`)) return;
    await deleteMut.mutateAsync(theme.id);
    // If the deleted theme was active, fall back to the seed it was cloned from.
    if (activeCustomThemeId === theme.id) {
      const seed = getSeedTheme(theme.seedThemeId);
      if (seed) useUiStore.getState().setTheme(seed.id as never);
    }
  };

  const onDuplicate = (theme: CustomTheme): void => {
    setCreatingFromSeed(theme.seedThemeId);
    // Defer the actual editor open until the next render so initialTokens flows.
    setTimeout(() => {
      setEditing({ ...theme, id: '', name: `${theme.name} (copy)` });
    }, 0);
  };

  const onImportClick = (): void => {
    setImportError(null);
    fileInputRef.current?.click();
  };

  const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so picking the same file twice still fires
    if (!file) return;
    let text: string;
    try {
      text = await file.text();
    } catch (err) {
      setImportError(`Couldn't read the file: ${String(err)}`);
      return;
    }
    const parsed = parseThemeJson(text);
    if (typeof parsed === 'string') {
      setImportError(parsed);
      return;
    }
    try {
      const created = await createMut.mutateAsync({
        name: parsed.name,
        seedThemeId: parsed.seedThemeId,
        tokens: parsed.tokens,
      });
      log.info('theme_imported', { name: created.name, id: created.id });
      setImportError(null);
    } catch (err) {
      setImportError(`Save failed: ${String(err)}`);
    }
  };

  const isActive = (id: string): boolean =>
    currentTheme === 'custom' && activeCustomThemeId === id;

  return (
    <SettingsCard
      id="custom-themes"
      title="Custom themes"
      desc="Clone a built-in palette, edit each color, share as JSON."
      fullWidth
    >
      <div className="custom-themes-bar">
        <div className="custom-themes-bar__new">
          <span className="custom-themes-bar__label">New from…</span>
          {SEED_THEMES.map((seed) => (
            <button
              key={seed.id}
              type="button"
              className="custom-themes-bar__seed"
              onClick={() => onCreateFromSeed(seed.id)}
              title={seed.hint}
            >
              {seed.label}
            </button>
          ))}
        </div>
        <div className="custom-themes-bar__io">
          <button type="button" className="btn" onClick={onImportClick}>
            Import JSON…
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={(e) => void onImportFile(e)}
          />
        </div>
      </div>

      {importError !== null && (
        <div className="custom-themes-error">Import failed: {importError}</div>
      )}

      {themes.isLoading && (
        <div className="custom-themes-empty">Loading your themes…</div>
      )}

      {themes.isSuccess && themes.data.length === 0 && (
        <div className="custom-themes-empty">
          You haven't created any themes yet. Pick a starting point from
          "New from…" above to clone one of the built-in palettes.
        </div>
      )}

      {themes.isSuccess && themes.data.length > 0 && (
        <ul className="custom-themes-list">
          {themes.data.map((t) => (
            <li key={t.id} className="custom-themes-row">
              <div className="custom-themes-row__swatch" aria-hidden="true">
                <span style={{ background: t.tokens['bg-0'] ?? 'transparent' }} />
                <span style={{ background: t.tokens['bg-1'] ?? 'transparent' }} />
                <span style={{ background: t.tokens['accent'] ?? 'transparent' }} />
                <span style={{ background: t.tokens['primary'] ?? 'transparent' }} />
              </div>
              <div className="custom-themes-row__meta">
                <div className="custom-themes-row__name">
                  {t.name}
                  {isActive(t.id) && (
                    <span className="custom-themes-row__active">active</span>
                  )}
                </div>
                <div className="custom-themes-row__sub">
                  Based on {t.seedThemeId}
                </div>
              </div>
              <div className="custom-themes-row__actions">
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => setCustomTheme(t.id)}
                  disabled={isActive(t.id)}
                >
                  {isActive(t.id) ? 'Applied' : 'Apply'}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setEditing(t)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => onDuplicate(t)}
                >
                  Duplicate
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => exportThemeToJson(t)}
                >
                  Export
                </button>
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => void onDelete(t)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Editor modal — render once at a time. */}
      {creatingFromSeed !== null && editing === null && (
        <ThemeEditor
          initial={null}
          initialTokens={
            getSeedTheme(creatingFromSeed)?.tokens ?? null
          }
          seedThemeId={creatingFromSeed}
          initialName={`${getSeedTheme(creatingFromSeed)?.label ?? creatingFromSeed} (mine)`}
          onSave={(name, tokens) => onSaveNew(name, tokens)}
          onCancel={() => setCreatingFromSeed(null)}
        />
      )}
      {editing !== null && (
        <ThemeEditor
          initial={editing.id.length > 0 ? editing : null}
          initialTokens={
            editing.id.length === 0
              ? (editing.tokens as Record<EditableTokenKey, string>)
              : null
          }
          seedThemeId={editing.seedThemeId}
          initialName={editing.name}
          onSave={async (name, tokens) => {
            if (editing.id.length === 0) {
              // Duplicate flow — save as a new theme.
              const created = await createMut.mutateAsync({
                name,
                seedThemeId: editing.seedThemeId,
                tokens,
              });
              setEditing(null);
              setCreatingFromSeed(null);
              setCustomTheme(created.id);
            } else {
              await onSaveEdit(name, tokens);
            }
          }}
          onCancel={() => {
            setEditing(null);
            setCreatingFromSeed(null);
          }}
        />
      )}
    </SettingsCard>
  );
}
